/**
 * Dependencies
 */
import { Camera, Geometry, Mesh, Plane, Program, Renderer, RenderTarget } from 'ogl'
import vertex from '../shaders/vertex.glsl'
import fragment from '../shaders/fragment.glsl'
import asciiVertex from '../shaders/ascii-vertex.glsl'
import asciiFragment from '../shaders/ascii-fragment.glsl'

const renderer = new Renderer()
const gl = renderer.gl
gl.clearColor(0, 0, 0, 1)
document.body.appendChild(gl.canvas)

const camera = new Camera(gl, { near: 0.1, far: 100 })
camera.position.set(0, 0, 3)

// Variables for animation and scrolling
let animationDirection = 0 // 1 for showing content, -1 for hiding content
let isAnimating = false
let animationProgress = 0
const animationSpeed = 0.022
// After the intro the content is visible; scrolling up reverses it.
let lastStopTime = 0
const gestureCooldown = 350 // ms; keeps inertia from starting or reversing a step by accident

function startStep(direction) {
  if (isAnimating || performance.now() - lastStopTime < gestureCooldown) return
  if (direction > 0) {
    if (animationProgress < 1) {
      animationDirection = 1
      isAnimating = true
    }
  } else if (animationProgress > 0) {
    animationDirection = -1
    isAnimating = true
  }
}

// Slow down the part of the animation that looks best (progress range, speed multiplier)
const slowZone = { start: 0.25, end: 0.55, speed: 0.4, edge: 0.05 }

function getSpeedMultiplier(progress) {
  const smooth = (a, b, x) => {
    const t = Math.max(0, Math.min((x - a) / (b - a), 1))
    return t * t * (3 - 2 * t)
  }
  const inZone =
    smooth(slowZone.start, slowZone.start + slowZone.edge, progress) *
    (1 - smooth(slowZone.end - slowZone.edge, slowZone.end, progress))
  return 1 - (1 - slowZone.speed) * inZone
}

// Get section element for fade-in effect
const section = document.querySelector('.section')

// Prevent transition flash on page load
window.addEventListener('DOMContentLoaded', () => {
  splitTextIntoLines()
  document.fonts?.ready.then(splitTextIntoLines)
  animateLogo()

  // Position the content at the bottom left immediately
  const content = document.querySelector('.content')
  if (content) {
    content.style.position = 'absolute'
    content.style.bottom = '0'
    content.style.left = '0'
  }

  // Initialize custom scroll handling
  initCustomScroll()
})

// Animation loop for smooth content transitions
let lastLoopTime = 0
function animationLoop(now = performance.now()) {
  // Normalise to 60fps so speed is identical on 60/120/144Hz displays
  const frameScale = lastLoopTime ? Math.min((now - lastLoopTime) / 16.667, 3) : 1
  lastLoopTime = now

  if (isAnimating) {
    if (animationDirection > 0) {
      animationProgress += animationSpeed * frameScale * getSpeedMultiplier(animationProgress)
      if (animationProgress >= 1) {
        animationProgress = 1
        isAnimating = false
      }
    } else {
      animationProgress -= animationSpeed * frameScale * getSpeedMultiplier(animationProgress)
      if (animationProgress <= 0) {
        animationProgress = 0
        isAnimating = false
      }
    }
    if (!isAnimating) lastStopTime = performance.now()

    // Apply animation progress to both canvas and content
    updateCanvasAndContentOpacity(animationProgress)
  }

  requestAnimationFrame(animationLoop)
}

// Logo intro: each character rises out of a mask, left to right
const logoReveal = {
  delay: 300, // ms before the first character
  stagger: 25, // ms between characters
}

function animateLogo() {
  const logo = document.querySelector('.header-logo')
  if (!logo || logo.dataset.split) return
  logo.dataset.split = 'true'
  const text = logo.textContent.trim()
  logo.setAttribute('aria-label', text)
  logo.textContent = ''
  ;[...text].forEach((char, i) => {
    const mask = document.createElement('span')
    mask.className = 'char'
    mask.setAttribute('aria-hidden', 'true')
    const inner = document.createElement('span')
    inner.className = 'char-inner'
    inner.textContent = char === ' ' ? '\u00a0' : char
    inner.style.animationDelay = `${logoReveal.delay + i * logoReveal.stagger}ms`
    mask.appendChild(inner)
    logo.appendChild(mask)
  })
}

// Text reveal: each line of text rises out of a mask, staggered, driven by progress
const textReveal = {
  start: 0.45, // progress where the first line starts to rise
  stagger: 0.35, // share of the reveal used to delay later lines (0 = all at once)
  distance: 110, // start offset in % of the line height
}
let textLines = []
let lastTextWidth = 0

function splitTextIntoLines() {
  if (!section) return
  const paragraphs = section.querySelectorAll('p')
  paragraphs.forEach((p) => {
    // Keep the original text so we can re-split on resize and font load
    if (!p.dataset.text) p.dataset.text = p.textContent.trim().replace(/\s+/g, ' ')
    p.textContent = ''
    const words = p.dataset.text.split(' ').map((word, i) => {
      const span = document.createElement('span')
      span.textContent = word
      p.append(span, i < p.dataset.text.split(' ').length - 1 ? ' ' : '')
      return span
    })

    // Group words by their rendered line
    const lines = []
    let lastTop = null
    words.forEach((span) => {
      const top = Math.round(span.getBoundingClientRect().top)
      if (lastTop === null || Math.abs(top - lastTop) > 2) {
        lines.push([])
        lastTop = top
      }
      lines[lines.length - 1].push(span.textContent)
    })

    p.textContent = ''
    lines.forEach((lineWords) => {
      const mask = document.createElement('span')
      mask.className = 'line'
      const inner = document.createElement('span')
      inner.className = 'line-inner'
      inner.textContent = lineWords.join(' ')
      mask.appendChild(inner)
      p.appendChild(mask)
    })
  })
  textLines = [...section.querySelectorAll('.line-inner')]
  lastTextWidth = section.clientWidth
  section.dataset.ready = 'true'
  updateCanvasAndContentOpacity(animationProgress)
}

// Drives the particle swarm and the text reveal from the same progress value
function updateCanvasAndContentOpacity(progress) {
  // Clamp progress between 0 and 1
  progress = Math.max(0, Math.min(progress, 1))

  // Drives the particle swarm (0 = particles in place, 1 = swarm gone)
  asciiProgram.uniforms.uScrollFade.value = progress

  const total = textLines.length
  if (!total) return
  const reveal = Math.max(0, Math.min((progress - textReveal.start) / (1 - textReveal.start), 1))
  const span = 1 - textReveal.stagger
  textLines.forEach((line, i) => {
    const delay = total > 1 ? (i / (total - 1)) * textReveal.stagger : 0
    const t = Math.max(0, Math.min((reveal - delay) / span, 1))
    const eased = 1 - Math.pow(1 - t, 3) // easeOutCubic
    line.style.transform = `translate3d(0, ${(1 - eased) * textReveal.distance}%, 0)`
  })
}

// Initialize custom scroll handling
function initCustomScroll() {
  // Ensure we handle all relevant touch and mouse events
  window.addEventListener('wheel', handleWheelEvent, { passive: false })
  window.addEventListener('touchstart', handleTouchStart, { passive: true })
  window.addEventListener('touchmove', handleTouchMove, { passive: false })
  window.addEventListener('touchend', handleTouchEnd)

  // Start the animation loop
  requestAnimationFrame(animationLoop)
}

// Touch event handlers
let lastTouchY = 0
let touchStartY = 0
let minTouchDistance = 20 // Minimum distance to trigger animation
let touchActive = false

function handleTouchStart(e) {
  touchActive = true
  lastTouchY = e.touches[0].clientY
  touchStartY = lastTouchY
}

function handleTouchMove(e) {
  if (!touchActive) return

  const currentY = e.touches[0].clientY
  const deltaY = lastTouchY - currentY
  lastTouchY = currentY

  // Prevent default to stop native scrolling
  e.preventDefault()

  // Accumulate touch movement
  const totalMovement = touchStartY - currentY

  // Check if we've moved enough to trigger animation
  if (!isAnimating && Math.abs(totalMovement) > minTouchDistance) {
    startStep(totalMovement > 0 ? 1 : -1)
  }
}

function handleTouchEnd() {
  touchActive = false
}

const wheelThreshold = 4 // minimum deltaY to count as a scroll

// Wheel event handler with improved handling
function handleWheelEvent(e) {
  // Prevent default to disable native scrolling
  e.preventDefault()

  // Ignore events that aren't a real vertical scroll (deltaY of 0, tiny trackpad
  // noise, horizontal or pinch-zoom wheel events)
  if (e.ctrlKey || Math.abs(e.deltaY) < wheelThreshold || Math.abs(e.deltaY) < Math.abs(e.deltaX))
    return

  // Determine scroll direction
  const direction = e.deltaY > 0 ? 1 : -1

  startStep(direction)
}

// Remove the old scroll event listener
window.removeEventListener('scroll', () => {})

const resize = () => {
  if (section && section.clientWidth !== lastTextWidth && textLines.length) splitTextIntoLines()
  renderer.setSize(window.innerWidth, window.innerHeight)
  camera.perspective({ aspect: gl.canvas.width / gl.canvas.height })
}
window.addEventListener('resize', resize)
resize()

// Setup Perlin noise shader
const perlinProgram = new Program(gl, {
  vertex,
  fragment,
  uniforms: {
    uTime: { value: 0 },
    uFrequency: { value: 2.0 },
    uBrightness: { value: 0.5 },
    uSpeed: { value: 0.75 },
    uValue: { value: 0.6 }, // Start with a lower lightness value
  },
})

const perlinMesh = new Mesh(gl, {
  geometry: new Plane(gl, { width: 2, height: 2 }),
  program: perlinProgram,
})

const renderTarget = new RenderTarget(gl)

// Setup ASCII shader
const asciiProgram = new Program(gl, {
  vertex: asciiVertex,
  fragment: asciiFragment,
  uniforms: {
    uResolution: { value: [gl.canvas.width, gl.canvas.height] },
    uTexture: { value: renderTarget.texture },
    uScrollFade: { value: 0.0 }, // 0.0 = particles in place, 1.0 = swarm gone
    uTime: { value: 0 },
    uStagger: { value: 0.3 },
    uDriftX: { value: 0.0 },
    uDriftY: { value: 0.05 },
    uRadius: { value: 0.03 },
    uRadiusVar: { value: 0.12 },
    uOrbitSpeed: { value: 1.5 },
    uOrbitVar: { value: 2.0 },
    uJitter: { value: 3.0 },
    uScatter: { value: 0.45 },
    uGatherEnd: { value: 0.1 },
    uFadeLength: { value: 0.4 },
    uPointSize: { value: 8.0 },
  },
  transparent: false,
  depthTest: false,
  depthWrite: false,
})

// One point per 8px ASCII cell; the vertex shader moves them into a swarm
const CELL_SIZE = 8
function createParticleGeometry() {
  const cols = Math.ceil(gl.canvas.width / CELL_SIZE)
  const rows = Math.ceil(gl.canvas.height / CELL_SIZE)
  const count = cols * rows
  const positions = new Float32Array(count * 2)
  const randoms = new Float32Array(count * 3)
  for (let i = 0; i < count; i++) {
    positions[i * 2] = ((i % cols) + 0.5) * CELL_SIZE
    positions[i * 2 + 1] = (Math.floor(i / cols) + 0.5) * CELL_SIZE
    randoms[i * 3] = Math.random()
    randoms[i * 3 + 1] = Math.random()
    randoms[i * 3 + 2] = Math.random()
  }
  return new Geometry(gl, {
    position: { size: 2, data: positions },
    aRandom: { size: 3, data: randoms },
  })
}

const asciiMesh = new Mesh(gl, {
  geometry: createParticleGeometry(),
  program: asciiProgram,
  mode: gl.POINTS,
  frustumCulled: false,
})

window.addEventListener('resize', () => {
  asciiMesh.geometry = createParticleGeometry()
})

// Set up frame rate limiting
let lastTime = 0
const frameRate = 60 // Target frame rate in frames per second
const frameInterval = 1000 / frameRate - 2 // tolerance avoids dropping frames to rAF jitter

// Background noise runs slower while resting at the top (progress 0)
const restNoiseSpeed = 0.6 // 1 = same as normal
const restBlendRange = 0.3 // progress over which it blends back to normal speed
let noiseTime = 0
let lastNoiseTime = 0
const smoothstep01 = (a, b, x) => {
  const t = Math.max(0, Math.min((x - a) / (b - a), 1))
  return t * t * (3 - 2 * t)
}

function update(time) {
  requestAnimationFrame(update)

  // Only update if enough time has passed since the last frame
  if (time - lastTime < frameInterval) return
  lastTime = time

  // Rendering code
  const elapsedTime = time * 0.001
  // Accumulate noise time so speed changes never make the pattern jump
  const dt = lastNoiseTime ? elapsedTime - lastNoiseTime : 0
  lastNoiseTime = elapsedTime
  const rest = 1 - smoothstep01(0, restBlendRange, animationProgress)
  noiseTime += dt * (1 - (1 - restNoiseSpeed) * rest)
  perlinProgram.uniforms.uTime.value = noiseTime
  asciiProgram.uniforms.uTime.value = elapsedTime

  // Render Perlin noise to render target
  renderer.render({ scene: perlinMesh, camera, target: renderTarget })

  // Render ASCII shader to screen
  asciiProgram.uniforms.uResolution.value = [gl.canvas.width, gl.canvas.height]
  renderer.render({ scene: asciiMesh, camera })
}

// Start the render loop
requestAnimationFrame(update)

// Tuning panel (toggle with the "T" key, or add ?tune to the URL)
const tuneParams = [
  ['uStagger', 'Stagger', 0, 0.95, 0.01],
  ['uGatherEnd', 'Fade start', 0, 0.95, 0.01],
  ['uFadeLength', 'Fade length', 0.05, 1, 0.01],
  ['uDriftX', 'Wind X', -0.5, 0.5, 0.01],
  ['uDriftY', 'Wind Y', -0.5, 0.5, 0.01],
  ['uRadius', 'Orbit radius', 0, 0.3, 0.005],
  ['uRadiusVar', 'Orbit radius var', 0, 0.5, 0.005],
  ['uOrbitSpeed', 'Orbit speed', 0, 10, 0.1],
  ['uOrbitVar', 'Orbit speed var', 0, 15, 0.1],
  ['uJitter', 'Jitter (px)', 0, 40, 0.5],
  ['uScatter', 'Scatter', 0, 2, 0.01],
  ['uPointSize', 'Point size (px)', 2, 24, 0.5],
]

function createTunePanel() {
  const panel = document.createElement('div')
  panel.style.cssText =
    'position:fixed;top:8px;right:8px;z-index:1000;width:240px;padding:10px;background:rgba(0,0,0,.8);color:#fff;font:11px monospace;border:1px solid #444;max-height:95vh;overflow:auto;'
  panel.addEventListener('wheel', (e) => e.stopPropagation(), { capture: true })
  panel.addEventListener('touchmove', (e) => e.stopPropagation(), { capture: true })
  const addRow = (label, min, max, step, value, onInput) => {
    const row = document.createElement('label')
    row.style.cssText = 'display:block;margin-bottom:6px;'
    const text = document.createElement('span')
    const input = document.createElement('input')
    input.type = 'range'
    Object.assign(input, { min, max, step, value })
    input.style.width = '100%'
    const update = () => {
      text.textContent = `${label}: ${Number(input.value).toFixed(3)}`
      onInput(parseFloat(input.value))
    }
    input.addEventListener('input', update)
    text.textContent = `${label}: ${Number(value).toFixed(3)}`
    row.append(text, input)
    panel.appendChild(row)
    return input
  }

  // Manual scrub overrides the scroll animation while dragging
  addRow('Progress (scrub)', 0, 1, 0.005, animationProgress, (v) => {
    isAnimating = false
    animationProgress = v
    animationDirection = 0
    updateCanvasAndContentOpacity(v)
  })

  tuneParams.forEach(([name, label, min, max, step]) => {
    addRow(label, min, max, step, asciiProgram.uniforms[name].value, (v) => {
      asciiProgram.uniforms[name].value = v
    })
  })

  const copy = document.createElement('button')
  copy.textContent = 'Copy values'
  copy.style.cssText = 'width:100%;margin-top:4px;'
  copy.addEventListener('click', () => {
    const out = Object.fromEntries(tuneParams.map(([n]) => [n, asciiProgram.uniforms[n].value]))
    navigator.clipboard?.writeText(JSON.stringify(out, null, 2))
    console.log(out)
  })
  panel.appendChild(copy)
  return panel
}

let tunePanel = null
function toggleTunePanel() {
  if (tunePanel) {
    tunePanel.remove()
    tunePanel = null
  } else {
    tunePanel = createTunePanel()
    document.body.appendChild(tunePanel)
  }
}
if (new URLSearchParams(location.search).has('tune')) toggleTunePanel()
window.addEventListener('keydown', (e) => {
  if (e.key.toLowerCase() === 't' && !e.metaKey && !e.ctrlKey) toggleTunePanel()
})
